import React, { useEffect, useState, useCallback } from "react";
import { KeyRound, Loader2, Plus, Copy, Check, Trash2, Ban, RefreshCw, Terminal, X } from "lucide-react";
import { apiKeysList, apiKeyCreate, apiKeyRevoke, apiKeyDelete, BACKEND_URL } from "../lib/api";
import { toast } from "sonner";

const BASE = `${BACKEND_URL}/api/v1`;

const ENDPOINTS = [
  { m: "POST", p: "/v1/ocr", d: "Photo/PDF → extracted text (multipart: files)" },
  { m: "POST", p: "/v1/draft", d: "AI writes a letter/email/reply (JSON: task, instructions, tone, source_text)" },
  { m: "POST", p: "/v1/pdf/compose", d: "Images → clean PDF (multipart: files, bw)" },
  { m: "POST", p: "/v1/pdf/merge", d: "Merge several PDFs into one (multipart: files)" },
  { m: "POST", p: "/v1/sign", d: "Signed PDF from text + signature image + attachments (multipart)" },
];

export default function DeveloperApi({ userId }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [limit, setLimit] = useState("0");
  const [creating, setCreating] = useState(false);
  const [reveal, setReveal] = useState(null); // freshly created full key
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await apiKeysList(userId); setKeys(r.keys || []); }
    catch (e) { toast.error("Could not load API keys"); }
    finally { setLoading(false); }
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!name.trim()) { toast.message("Give the key a name (e.g. the app it's for)."); return; }
    setCreating(true);
    try {
      const r = await apiKeyCreate(userId, name.trim(), Math.max(0, parseInt(limit || "0", 10)));
      setReveal(r.key); setName(""); setLimit("0"); await load();
    } catch (e) { toast.error("Could not create key"); }
    finally { setCreating(false); }
  };

  const revoke = async (k) => { if (!window.confirm(`Revoke "${k.name}"? Apps using it will stop working.`)) return; try { await apiKeyRevoke(k.id, userId); toast.success("Revoked"); load(); } catch (e) { toast.error("Could not revoke"); } };
  const del = async (k) => { if (!window.confirm(`Delete "${k.name}" permanently?`)) return; try { await apiKeyDelete(k.id, userId); setKeys((p) => p.filter((x) => x.id !== k.id)); toast.success("Deleted"); } catch (e) { toast.error("Could not delete"); } };
  const copyKey = () => { navigator.clipboard.writeText(reveal).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }); };

  const inp = "w-full bg-[#0a1526] text-cyan-50 border border-[#00F0FF33] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#00F0FF]";

  return (
    <div className="lg:col-span-2 nx-glass rounded-2xl p-4 space-y-4" data-testid="developer-api-panel">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "radial-gradient(circle at 40% 30%, #00F0FF, #061018 75%)", boxShadow: "0 0 20px #00F0FF66" }}><KeyRound className="w-5 h-5 text-white" /></div>
        <div>
          <h2 className="nx-orbitron font-black tracking-[0.2em] text-[#00F0FF] text-base leading-none">DEVELOPER API</h2>
          <p className="text-cyan-100/45 text-[10px] nx-mono mt-0.5">Embed SnapDoc &amp; DigiSign in your apps · free for internal use</p>
        </div>
        <button onClick={load} data-testid="api-refresh" className="ml-auto text-cyan-200/50 hover:text-[#00F0FF]"><RefreshCw className="w-4 h-4" /></button>
      </div>

      {/* create */}
      <div className="rounded-xl bg-[#0a1526] border border-[#00F0FF22] p-3 space-y-2">
        <p className="text-cyan-200/60 text-[11px] nx-mono tracking-widest">CREATE A KEY</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input className={inp} value={name} onChange={(e) => setName(e.target.value)} placeholder="Key name (e.g. My website)" data-testid="api-key-name" />
          <input className={`${inp} sm:w-56`} type="number" min="0" value={limit} onChange={(e) => setLimit(e.target.value)} placeholder="Monthly limit (0 = unlimited)" data-testid="api-key-limit" />
          <button onClick={create} disabled={creating} data-testid="api-key-create" className="px-4 py-2.5 rounded-xl nx-orbitron font-bold text-[#030712] bg-[#00F0FF] disabled:opacity-40 flex items-center justify-center gap-2 whitespace-nowrap">{creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create</button>
        </div>
        <p className="text-cyan-100/35 text-[10px] nx-mono">Leave limit at 0 for now — free and unlimited for your own apps. Add a monthly limit later when you sell access.</p>
      </div>

      {/* list */}
      {loading ? (
        <div className="py-10 flex items-center justify-center"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>
      ) : keys.length === 0 ? (
        <p className="text-cyan-100/30 text-sm nx-mono text-center py-6">No API keys yet — create one above.</p>
      ) : (
        <div className="space-y-2" data-testid="api-keys-list">
          {keys.map((k) => (
            <div key={k.id} data-testid="api-key-item" className={`flex items-center gap-3 p-3 rounded-xl bg-[#0a1526] border ${k.active ? "border-[#00F0FF22]" : "border-red-500/30 opacity-70"}`}>
              <KeyRound className={`w-4 h-4 shrink-0 ${k.active ? "text-[#00F0FF]" : "text-red-400"}`} />
              <div className="flex-1 min-w-0">
                <p className="text-cyan-50 text-sm font-semibold truncate">{k.name} {!k.active && <span className="text-[10px] text-red-400 nx-mono">· revoked</span>}</p>
                <p className="text-cyan-100/40 text-[11px] nx-mono truncate">{k.prefix}…· used {k.usage_month} {k.monthly_limit > 0 ? `/ ${k.monthly_limit}` : "(unlimited)"} this month</p>
              </div>
              {k.active && <button onClick={() => revoke(k)} data-testid="api-key-revoke" title="Revoke" className="p-2 rounded-lg text-amber-400 hover:bg-amber-500/10"><Ban className="w-4 h-4" /></button>}
              <button onClick={() => del(k)} data-testid="api-key-delete" title="Delete" className="p-2 rounded-lg text-cyan-100/40 hover:text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      {/* docs */}
      <div className="rounded-xl bg-[#0a1526] border border-[#00F0FF22] p-3 space-y-2">
        <p className="text-cyan-200/60 text-[11px] nx-mono tracking-widest flex items-center gap-1"><Terminal className="w-3.5 h-3.5" /> ENDPOINTS · base {BASE}</p>
        <div className="space-y-1.5" data-testid="api-docs">
          {ENDPOINTS.map((e) => (
            <div key={e.p} className="flex gap-2 items-start text-[11px] nx-mono">
              <span className="text-[#25D366] font-bold shrink-0">{e.m}</span>
              <span className="text-[#00F0FF] shrink-0">{e.p}</span>
              <span className="text-cyan-100/50">{e.d}</span>
            </div>
          ))}
        </div>
        <pre className="text-[10px] nx-mono text-cyan-100/60 bg-black/40 rounded-lg p-2 overflow-x-auto whitespace-pre">{`curl -X POST ${BASE}/draft \\
  -H "Authorization: Bearer YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"task":"email","instructions":"Chase the invoice","tone":"Firm"}'`}</pre>
        <p className="text-cyan-100/35 text-[10px] nx-mono">Send your key as <b className="text-cyan-100/60">Authorization: Bearer &lt;key&gt;</b> or <b className="text-cyan-100/60">X-API-Key</b>. Invalid/revoked → 401, over the monthly limit → 429.</p>
      </div>

      {/* reveal-once modal */}
      {reveal && (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="api-key-reveal">
          <div className="w-full max-w-md bg-[#071018] border border-[#00F0FF44] rounded-2xl p-5 space-y-3">
            <div className="flex items-center justify-between">
              <p className="nx-orbitron font-black tracking-widest text-[#00F0FF] text-sm flex items-center gap-2"><KeyRound className="w-4 h-4" /> YOUR NEW KEY</p>
              <button onClick={() => setReveal(null)} data-testid="api-reveal-close" className="text-cyan-200/50 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-amber-300 text-[11px] nx-mono">Copy it now — for your security it's shown only once.</p>
            <div className="flex items-center gap-2">
              <code data-testid="api-reveal-key" className="flex-1 text-[12px] text-cyan-50 bg-black/50 rounded-lg p-2.5 break-all">{reveal}</code>
              <button onClick={copyKey} data-testid="api-reveal-copy" className="px-3 py-2.5 rounded-lg text-[#030712] bg-[#00F0FF] flex items-center gap-1 nx-mono text-xs font-bold">{copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}</button>
            </div>
            <button onClick={() => setReveal(null)} data-testid="api-reveal-done" className="w-full py-2.5 rounded-xl nx-mono text-xs font-bold text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11]">Done</button>
          </div>
        </div>
      )}
    </div>
  );
}
